/* eslint-disable no-restricted-globals */
import { useEffect, useRef, useState } from 'react';

import { THEME } from 'src/enums/theme';

type ThemeConfig = {
  value: THEME.LIGHT | THEME.DARK;
};

type RouteThemeMap = Record<string, ThemeConfig>;

export const DEFAULT_THEME: ThemeConfig = {
  value: THEME.LIGHT,
};

// Define the default route themes
const THEME_BY_ROUTE: RouteThemeMap = {
  '/': DEFAULT_THEME,
  '/credits': { value: THEME.DARK },
  '/credits/storefront': { value: THEME.DARK },
  '/credits/store': { value: THEME.DARK },

  // ASX Credits Frontend Routes
  // Credits section
  '/credits/interest': { value: THEME.LIGHT },
  '/credits/interest/*': { value: THEME.LIGHT },
  '/credits/product': { value: THEME.LIGHT },
  '/credits/product/*': { value: THEME.LIGHT },
  '/credits/faq': { value: THEME.LIGHT },
  '/credits/segments/*': { value: THEME.LIGHT },

  // Account section
  '/account': { value: THEME.LIGHT },
  '/account/*': { value: THEME.LIGHT },
};

/**
 * Find matching theme config for a given path, supporting wildcards.
 * Prioritizes exact matches first, then sorts wildcard patterns by
 * specificity (longest first).
 */
const findThemeConfig = (path: string): ThemeConfig => {
  // First try exact match
  const exactMatch = THEME_BY_ROUTE[path];

  if (exactMatch) {
    return exactMatch;
  }

  // For wildcard matches, sort by specificity (length) first
  const matchingPattern = Object.keys(THEME_BY_ROUTE)
    .filter((pattern) => pattern.endsWith('/*'))
    .sort((a, b) => b.length - a.length)
    .find((pattern) => {
      const baseRoute = pattern.slice(0, -2);

      return path === baseRoute || path.startsWith(`${baseRoute}/`);
    });

  return matchingPattern ? THEME_BY_ROUTE[matchingPattern] : DEFAULT_THEME;
};

const getCurrentThemeConfig = (): ThemeConfig => {
  const path = location.pathname.replace(/\/+$/, '') || '/';

  return findThemeConfig(path);
};

/**
 * Returns the theme config for the current page route.
 */
export const useThemeConfig = () => {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(getCurrentThemeConfig);

  const previousUrl = useRef(location.href);
  const observer = useRef<MutationObserver>();

  // Utilize a MutationObserver against the root document to listen for URL changes
  // https://tiny.amazon.com/1s8r9h47/url-listening
  // https://tiny.amazon.com/w7rjjvqw/mutation-observers
  if (!observer.current) {
    observer.current = new MutationObserver(() => {
      if (location.href === previousUrl.current) {
        return;
      }

      previousUrl.current = location.href;
      setThemeConfig(getCurrentThemeConfig());
    });
  }

  useEffect(() => {
    const currentObserver = observer.current;

    currentObserver?.observe(document, {
      subtree: true,
      childList: true,
    });

    return () => {
      currentObserver?.disconnect();
    };
  }, []);

  return {
    themeConfig,
    observer: observer.current,
  };
};
